import { createContext, FunctionComponent } from 'preact';
import { SvpSeason } from '@vgtv/api-client/lib/svp_season';
import { useContext, useEffect, useMemo, useState } from 'preact/hooks';

import { isEpisode } from '../utils/assetHelpers';

import { useCurrentAsset } from './CurrentAssetProvider';
import { useApis } from './ApisProvider';
import { useAbortController } from './useAbortController';

interface SeriesContextProps {
  seasons: SvpSeason[];
}

const SeriesContext = createContext<SeriesContextProps>(undefined!);

export const SeriesProvider: FunctionComponent = ({ children }) => {
  const [seasons, setSeasons] = useState<SvpSeason[]>([]);
  const { asset } = useCurrentAsset();
  const { svpApi } = useApis();
  const { getSignal } = useAbortController();

  useEffect(() => {
    setSeasons([]);

    if (!asset || !isEpisode(asset)) {
      return;
    }

    const categoryId = asset.category.id;

    svpApi.seasons
      .getSeasons(categoryId, { signal: getSignal() })
      .then(setSeasons)
      .catch((err) => {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }

        console.error(err);
      });
  }, [asset, svpApi, getSignal]);

  const state = useMemo(() => {
    return {
      seasons,
    };
  }, [seasons]);

  return (
    <SeriesContext.Provider value={state}>{children}</SeriesContext.Provider>
  );
};

export const useSeries = () => {
  const series = useContext(SeriesContext);

  if (process.env.NODE_ENV !== 'production' && !series) {
    console.warn(
      'Series context is undefined, please verify you are calling useSeries() as child of a <SeriesProvider> component.'
    );
  }

  return series;
};
